import React from 'react';

import PropTypes from 'prop-types';
import SearchIcon from 'react-icons/lib/fa/search';
import { withRouter } from 'react-router';

import {
    FadeInOutContainer,
    FadeInOut,
} from 'wanderu-animations';
import {
    Button,
    LoadingDots,
    Section,
    Typography,
} from 'wanderu-components';
import {
    i18nMoment,
    localize,
} from 'wanderu-i18n';
import {
    buildSearchUrlPath,
    range,
} from 'wanderu-utils';

import TripItem from './TripItem/TripItem';
import Events from './events/UpcomingTripsEvents';
import PriceGrid from '../../PriceGrid/PriceGrid';

import styles from './UpcomingTrips.scss';

const DAYS_TO_SHOW = 7;

const propTypes = {
    departDate: PropTypes.string.isRequired,
    history: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    language: PropTypes.string,
    locale: PropTypes.string.isRequired,
    locationData: PropTypes.shape({
        arrival: PropTypes.shape({
            countryCode: PropTypes.string,
            countryName: PropTypes.string,
            formattedAddress: PropTypes.string,
            name: PropTypes.string,
            placeId: PropTypes.string,
            state: PropTypes.string,
        }),
        departure: PropTypes.shape({
            countryCode: PropTypes.string,
            countryName: PropTypes.string,
            formattedAddress: PropTypes.string,
            latitude: PropTypes.number,
            longitude: PropTypes.number,
            name: PropTypes.string,
            placeId: PropTypes.string,
            state: PropTypes.string,
        }),
    }).isRequired,
    namespace: PropTypes.string.isRequired,
    onDateSelect: PropTypes.func.isRequired,
    queries: PropTypes.object,
    t: PropTypes.func.isRequired,
    trips: PropTypes.arrayOf(PropTypes.object),
};
const defaultProps = {
    isLoading: false,
    language: '',
    trips: [],
};

function getFeaturedDates() {
    return range(DAYS_TO_SHOW).reduce((obj, item, index) => {
        const daysToAdd = index + 1;
        const upcomingDate = i18nMoment().add(daysToAdd, 'days').format('YYYY-MM-DD');

        return {
            ...obj,
            [upcomingDate]: '',
        };
    }, {});
}

function UpcomingTrips(props) {
    const {
        departDate,
        history,
        isLoading,
        language,
        locale,
        locationData,
        namespace,
        onDateSelect,
        queries,
        t,
        trips,
    } = props;
    const {
        placeId: destinationPlaceId,
        formattedAddress: formattedDestinationAddress,
    } = locationData.arrival;
    const {
        placeId: originPlaceId,
        formattedAddress: formattedOriginAddress,
    } = locationData.departure;

    const dates = getFeaturedDates();
    const defaultDate = Object.keys(dates)[0];

    if (!language && departDate === defaultDate && !trips.length) {
        return null;
    }

    const handleDateSelect = (date) => {
        onDateSelect(date);
    };

    function buildSearchUrl() {
        const searchUrlRequest = {
            arrivePlace: formattedDestinationAddress,
            arrivePlaceId: destinationPlaceId,
            departDate,
            departPlace: formattedOriginAddress,
            departPlaceId: originPlaceId,
        };

        return buildSearchUrlPath(searchUrlRequest, queries);
    }

    function trackEvent(index) {
        Events.tripSelected({
            date: departDate,
            index: typeof index === 'number'
                ? index
                : undefined,
        });
    }

    const searchUrl = buildSearchUrl();

    function onSeeMore() {
        trackEvent();

        const [searchPath, searchQueries] = searchUrl.split('?');
        history.push({
            pathname: `/${locale}${searchPath}`,
            search: `?${searchQueries}`,
        });
    }

    return (
        <Section
            classes={{ content: styles.container }}
            id="upcoming-trips-section"
        >
            <Typography
                align="center"
                className={styles.title}
                variant="h2"
            >
                {t(`${namespace}:timeTableTitle`)}
            </Typography>
            <PriceGrid
                activeDate={departDate}
                className={styles.priceGrid}
                featuredDates={dates}
                onDateSelect={handleDateSelect}
            />
            {isLoading && (
                <LoadingDots
                    className={styles.dotContainer}
                    dotClassName={styles.dot}
                />
            )}
            <FadeInOutContainer>
                <FadeInOut
                    className={styles.tripTableFade}
                    key={departDate}
                    unmountOnExit
                >
                    {trips.map((trip, index) => (
                        <TripItem
                            href={searchUrl}
                            key={trip.tripId}
                            locale={locale}
                            onClick={trackEvent}
                            position={index}
                            trip={trip}
                        />
                    ))}
                    {!isLoading && !trips.length && (
                        <div className={styles.notFound}>
                            <SearchIcon className={styles.searchIcon} />
                            <span className={styles.notFoundText}>
                                {t(`${namespace}:timeTableNotFound`)}
                            </span>
                        </div>
                    )}
                    <Button
                        className={styles.searchButton}
                        label={t(`${namespace}:timeTableSeeMore`)}
                        onClick={onSeeMore}
                    >
                        {t(`${namespace}:timeTableSeeMore`)}
                    </Button>
                </FadeInOut>
            </FadeInOutContainer>
        </Section>
    );
}

UpcomingTrips.propTypes = propTypes;
UpcomingTrips.defaultProps = defaultProps;

export { UpcomingTrips };
export default withRouter(localize(UpcomingTrips));
